/**
 * API Client Service
 *
 * Handles all API requests to the backend for fetching projects and related data.
 */

import brandConfig from '../config/brandConfig';

const API_BASE_URL = brandConfig.apiUrl;

/**
 * Fetch projects by country code
 * @param {string} countryCode - ISO country code (e.g., 'PE', 'MX', 'CL')
 * @returns {Promise<Object>} Response object with projects list, count, and bbox
 */
export async function fetchProjects(countryCode) {
    try {
        const response = await fetch(`${API_BASE_URL}/projects/public/by-country/${countryCode}`, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit', // No enviar cookies para peticiones públicas
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            if (response.status === 404) {
                // No projects found for this country
                return {
                    data: [],
                    count: 0,
                    bbox: null
                };
            }
            throw new Error(`Failed to fetch projects: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        return {
            data: result.data || [],
            count: result.count || 0,
            bbox: result.bbox_min_lng && result.bbox_min_lat && result.bbox_max_lng && result.bbox_max_lat
                ? {
                    minLng: result.bbox_min_lng,
                    minLat: result.bbox_min_lat,
                    maxLng: result.bbox_max_lng,
                    maxLat: result.bbox_max_lat
                }
                : null
        };
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
}

/**
 * Fetch all projects from all countries
 * @returns {Promise<Object>} Response object with all projects, count, and bbox
 */
export async function fetchAllProjects() {
    try {
        // Get all country codes from config
        const { COUNTRY_CONFIG } = require('../config/countryConfig');
        const countryCodes = Object.values(COUNTRY_CONFIG).map(config => config.code);

        // Fetch projects from all countries in parallel
        const promises = countryCodes.map(code => fetchProjects(code));
        const results = await Promise.allSettled(promises);

        // Combine all projects
        let allProjects = [];
        let allBbox = null;
        let totalCount = 0;

        results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value.data.length > 0) {
                allProjects = allProjects.concat(result.value.data);
                totalCount += result.value.count;

                // Calculate combined bbox
                if (result.value.bbox) {
                    if (!allBbox) {
                        allBbox = { ...result.value.bbox };
                    } else {
                        allBbox.minLng = Math.min(allBbox.minLng, result.value.bbox.minLng);
                        allBbox.minLat = Math.min(allBbox.minLat, result.value.bbox.minLat);
                        allBbox.maxLng = Math.max(allBbox.maxLng, result.value.bbox.maxLng);
                        allBbox.maxLat = Math.max(allBbox.maxLat, result.value.bbox.maxLat);
                    }
                }
            }
        });

        // Ordenar todos los proyectos: primero PUBLICADO, luego EDICION, y dentro de cada grupo por updated_at
        const sortedProjects = allProjects.sort((a, b) => {
            // Prioridad por status: PUBLICADO primero (0), EDICION después (1)
            const statusPriority = {
                'PUBLICADO': 0,
                'EDICION': 1
            };
            const priorityA = statusPriority[a.status] ?? 2;
            const priorityB = statusPriority[b.status] ?? 2;
            
            // Si tienen diferente prioridad, ordenar por prioridad
            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }
            
            // Si tienen la misma prioridad, ordenar por updated_at (más recientes primero)
            const dateA = a.updated_at ? new Date(a.updated_at) : new Date(0);
            const dateB = b.updated_at ? new Date(b.updated_at) : new Date(0);
            return dateB - dateA; // Orden descendente (más recientes primero)
        });

        return {
            data: sortedProjects,
            count: totalCount,
            bbox: allBbox
        };
    } catch (error) {
        console.error('Error fetching all projects:', error);
        throw error;
    }
}

/**
 * Fetch a single project by slug
 * @param {string} slug - Project slug (URL-friendly identifier)
 * @returns {Promise<Object>} Project data
 */
export async function fetchProjectBySlug(slug) {
    try {
        const response = await fetch(`${API_BASE_URL}/projects/by-slug/${slug}`, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch project: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching project by slug:', error);
        throw error;
    }
}

/**
 * Get unique cities from projects list
 * @param {Array} projects - List of projects
 * @returns {Array<string>} Unique cities
 */
export function getUniqueCities(projects) {
    if (!projects || !Array.isArray(projects)) return [];

    const cities = projects
        .map(p => p.location)
        .filter(Boolean)
        .filter((city, index, self) => self.indexOf(city) === index)
        .sort();

    return cities;
}

/**
 * Get price range from projects list
 * @param {Array} projects - List of projects
 * @returns {Object} Price range {min, max}
 */
export function getPriceRange(projects) {
    if (!projects || !Array.isArray(projects) || projects.length === 0) {
        return { min: 0, max: 1000000 };
    }

    const prices = projects
        .map(p => p.budget || 0)
        .filter(price => price > 0);

    if (prices.length === 0) {
        return { min: 0, max: 1000000 };
    }

    return {
        min: Math.min(...prices),
        max: Math.max(...prices)
    };
}

/**
 * Format currency value with symbol
 * @param {number} value - Numeric value
 * @param {string} symbol - Currency symbol
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, symbol = '$') {
    if (!value || isNaN(value)) return `${symbol}0`;

    return `${symbol}${Number(value).toLocaleString('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    })}`;
}
